import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtDate, StatusBadge, RiskPill } from "../lib/status";
import { IncidentStatusBadge, SeverityBadge, ImpactBadge, NotifBadge, INCIDENT_TYPE_META } from "../lib/incidents";
import { ArrowLeft, AlertOctagon, BellRing, CheckCircle2, Ban, Mail, AlertTriangle, Send, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { severityStyle, typeLabel } from "../lib/warnings";
import ReassignVehicleModal from "../components/ReassignVehicleModal";

export default function IncidentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reminders, setReminders] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/incidents/${id}`);
      setData(data);
      try {
        const r = await api.get(`/reminders`, { params: { incident_id: id } });
        setReminders(r.data);
      } catch (_) { /* optional */ }
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="text-slate-500" data-testid="incident-detail-loading">Loading…</div>;
  if (!data) return null;
  const i = data.incident;
  const isClosed = i.status === "resolved" || i.status === "cancelled";

  const notify = async (scope) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/incidents/${id}/notify`, { scope });
      toast.success(`Notified ${res.notified} customer${res.notified === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/incidents/${id}/resolve`);
      toast.success(`Incident resolved · ${res.safe_again_sent} safe-again email${res.safe_again_sent === 1 ? "" : "s"} sent`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelIncident = async () => {
    setBusy(true);
    try {
      await api.post(`/incidents/${id}/cancel`);
      toast.success("Incident marked false alarm");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="incident-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <AlertOctagon className="w-6 h-6 text-red-600" />
            <h1 className="font-display text-3xl font-bold">{i.title}</h1>
            <SeverityBadge severity={i.severity} />
            <IncidentStatusBadge status={i.status} data-testid="incident-status-badge" />
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {INCIDENT_TYPE_META[i.incident_type] || i.incident_type} ·
            <Link to={`/app/vehicles/${data.vehicle?.id}`} className="text-[#1B4332] hover:underline ml-1">{data.vehicle?.name}</Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isClosed && data.total_affected > 0 && (
            <>
              <button onClick={() => notify("all")} disabled={busy} className="px-4 py-2 rounded-lg btn-brand text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid="notify-all-btn">
                <BellRing className="w-4 h-4" /> Notify all affected
              </button>
              {data.counts.directly_affected > 0 && (
                <button onClick={() => notify("directly_affected")} disabled={busy} className="px-4 py-2 rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm inline-flex items-center gap-2" data-testid="notify-direct-btn">
                  Notify directly-affected
                </button>
              )}
              {data.counts.nearby_risk > 0 && (
                <button onClick={() => notify("nearby_risk")} disabled={busy} className="px-4 py-2 rounded-lg border border-amber-300 text-amber-800 bg-white hover:bg-amber-50 text-sm inline-flex items-center gap-2" data-testid="notify-nearby-btn">
                  Notify nearby-risk
                </button>
              )}
            </>
          )}
          {!isClosed && (
            <>
              <button onClick={resolve} disabled={busy} className="px-4 py-2 rounded-lg border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50 text-sm inline-flex items-center gap-2" data-testid="resolve-btn">
                <CheckCircle2 className="w-4 h-4" /> Mark resolved
              </button>
              <button onClick={cancelIncident} disabled={busy} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-2" data-testid="cancel-incident-btn">
                <Ban className="w-4 h-4" /> False alarm
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6 space-y-4">
          <h2 className="font-display text-lg font-semibold">Incident details</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Info label="Started" value={fmtDate(i.start_datetime)} />
            <Info label="Estimated resolved" value={fmtDate(i.estimated_resolved_datetime)} />
            {i.actual_resolved_datetime && <Info label="Actual resolved" value={fmtDate(i.actual_resolved_datetime)} />}
            <Info label="Severity" value={i.severity} />
            <Info label="Type" value={INCIDENT_TYPE_META[i.incident_type] || i.incident_type} />
            <Info label="Vehicle status update" value={i.vehicle_status_was_updated ? `Yes: was '${i.previous_vehicle_status}'` : "No"} />
          </div>
          {i.description && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</div>
              <p className="text-sm mt-1 whitespace-pre-line">{i.description}</p>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Affected bookings</div>
          <div className="mt-2 text-4xl font-display font-semibold">{data.total_affected}</div>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Directly affected</span> <span className="font-medium">{data.counts.directly_affected}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Nearby risk</span> <span className="font-medium">{data.counts.nearby_risk}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Watch only</span> <span className="font-medium">{data.counts.watch_only}</span></div>
          </div>
        </div>
      </div>

      <Group title="Directly affected" items={data.directly_affected} empty="No bookings overlap this incident." />
      <Group title="Nearby risk" items={data.nearby_risk} empty="No bookings within the buffer window." />
      <Group title="Watch only" items={data.watch_only} empty="No bookings inside the watch window." />

      <LinkedDamagesCard incidentId={id} vehicleId={data.vehicle?.id} />

      <OperationalWarningsForIncident incidentId={id} />

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="incident-reminders-card">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Reminders</h3>
          <Link to="/app/reminders" className="text-sm text-[#1B4332] hover:underline" data-testid="incident-reminders-all-link">View all →</Link>
        </div>
        {reminders.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">No reminders linked to this incident yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {reminders.map((r) => (
              <li key={r.id} className="py-3 text-sm" data-testid={`incident-reminder-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <span className="font-medium">{r.title}</span>
                  <span className="text-xs text-slate-500">{r.priority} · {r.status} · due {fmtDate(r.due_at)}</span>
                </div>
                <div className="text-xs text-slate-500 line-clamp-2">{r.message}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Group({ title, items, empty }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`group-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <span className="text-xs text-slate-500">{items.length} booking{items.length === 1 ? "" : "s"}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 mt-3">{empty}</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {items.map((item) => {
            const b = item.booking;
            return (
              <li key={item.affected_row_id} className="py-3" data-testid={`affected-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <Link to={`/app/bookings/${b.id}`} className="font-medium text-slate-900 hover:underline">{b.customer_name}</Link>
                    <div className="text-xs text-slate-500 font-mono">{b.booking_reference} · masked: {b.customer_name_masked}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ImpactBadge impact={item.impact_level} />
                    <NotifBadge status={item.notification_status} />
                    <StatusBadge status={b.status} />
                    <RiskPill risk={b.risk_status} />
                  </div>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {b.vehicle?.name} · {fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)}
                </div>
                {item.customer_notified_at && (
                  <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Notified {fmtDate(item.customer_notified_at)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function OperationalWarningsForIncident({ incidentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reassignBookingId, setReassignBookingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/warnings/incidents/${incidentId}/affected-bookings`);
      setItems(data.items || []);
    } catch (_) { /* noop */ }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [incidentId]);

  const notify = async (warningId) => {
    setBusyId(warningId);
    try {
      await api.post(`/warnings/${warningId}/notify-customer`, {});
      toast.success("Customer notified");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="incident-ops-warnings-loading">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Operational warnings
        </h3>
        <div className="text-sm text-slate-500 mt-3">Scanning…</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="incident-ops-warnings-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Operational warnings
        </h3>
        <span className="text-xs text-slate-500">{items.length} flagged booking{items.length === 1 ? "" : "s"}</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Bookings auto-flagged by our warning engine because this incident may affect them. We only message the customer when you click <span className="font-semibold">Notify</span>.
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 mt-3" data-testid="incident-ops-warnings-empty">
          No additional warnings tied to this incident.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {items.map(({ warning: w, booking: b }) => {
            const style = severityStyle(w.severity);
            return (
              <li key={w.id} className="py-3" data-testid={`incident-ops-warning-${w.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${style.title}`}>{style.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${style.chip}`}>{typeLabel(w.warning_type)}</span>
                      {b && (
                        <Link to={`/app/bookings/${b.id}`} className="text-sm font-medium text-slate-900 hover:underline">
                          {b.customer_name} · {b.booking_reference}
                        </Link>
                      )}
                    </div>
                    {b && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)} · {b.customer_email}
                      </div>
                    )}
                    <div className="text-sm text-slate-700 mt-1">{w.message}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {b && (
                      <button
                        onClick={() => setReassignBookingId(b.id)}
                        data-testid={`incident-ops-reassign-${w.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1B4332] text-[#1B4332] text-xs hover:bg-emerald-50"
                      >
                        <ArrowRightLeft className="w-3.5 h-3.5" /> Reassign
                      </button>
                    )}
                    <button
                      onClick={() => notify(w.id)}
                      disabled={busyId === w.id}
                      data-testid={`incident-ops-notify-${w.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-xs hover:opacity-90 disabled:opacity-60"
                    >
                      <Send className="w-3.5 h-3.5" /> {busyId === w.id ? "Sending…" : "Notify"}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ReassignVehicleModal
        bookingId={reassignBookingId}
        open={!!reassignBookingId}
        onClose={() => setReassignBookingId(null)}
        onDone={load}
      />

    </div>
  );
}


// ---------------------------------------------------------------------------
// Damages linked to this incident: bidirectional link from the damages module.
// ---------------------------------------------------------------------------
function LinkedDamagesCard({ incidentId, vehicleId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!incidentId) return;
    api.get(`/vehicles/incident-links/${incidentId}/damages`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [incidentId]);

  if (items === null) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="incident-linked-damages-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <AlertOctagon className="w-4 h-4 text-rose-600" /> Linked damage records
        </h3>
        <span className="text-xs text-slate-500">{items.length} record{items.length === 1 ? "" : "s"}</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Damage entries on this vehicle that staff have linked to this incident.
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 mt-3" data-testid="incident-linked-damages-empty">
          No damage records have been linked to this incident yet. Open the damage sheet to link one.
          {vehicleId && (
            <> {" "}
              <Link to={`/app/vehicles/${vehicleId}/damages`} className="text-[#1B4332] hover:underline">
                Open damage sheet →
              </Link>
            </>
          )}
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {items.map((d) => (
            <li key={d.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`incident-linked-damage-${d.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium capitalize">
                    {(d.tag_code || "damage").replace(/_/g, " ")}
                  </span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider">{d.severity}</span>
                  {d.status === "repaired" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 uppercase tracking-wider">Repaired</span>
                  )}
                </div>
                {d.notes && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{d.notes}</p>}
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Observed {d.observed_date || "-"}{d.created_by?.name ? ` · by ${d.created_by.name}` : ""}
                </p>
              </div>
              {vehicleId && (
                <Link
                  to={`/app/vehicles/${vehicleId}/damages`}
                  className="text-xs text-emerald-700 hover:underline"
                  data-testid={`incident-linked-damage-${d.id}-open`}
                >
                  Open damage sheet →
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
